import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DashboardStats } from "@/components/Dashboard/DashboardStats";
import { TransactionAddDialog } from "@/components/Transactions/TransactionAddDialog";
import { TransferDialog } from "@/components/Transactions/TransferDialog";
import { lazy, Suspense } from "react";
import { BankStatementImport } from "@/components/Transactions/BankStatementImport";
import { ImportSourceDialog } from "@/components/statement/ImportSourceDialog";
import { FEATURE_STATEMENT_PDF } from "@/lib/featureFlags";

// pdf.js + the whole PDF flow load only when the feature is on and opened.
const PdfStatementImport = lazy(() =>
  import("@/components/statement/PdfStatementImport").then((m) => ({ default: m.PdfStatementImport })),
);
import { TransactionList } from "@/components/Transactions/TransactionList";
import { FloatingChatBot } from "@/components/Chat/FloatingChatBot";
import { MoodTracker } from "@/components/Wellness/MoodTracker";
import { MoodIndicator } from "@/components/Dashboard/MoodIndicator";
import { DailyImpulse } from "@/components/Dashboard/DailyImpulse";
import { GoalSpotlight } from "@/components/Dashboard/GoalSpotlight";
import { QuickActions } from "@/components/Dashboard/QuickActions";
import { SkeletonLoader } from "@/components/ui/skeleton-loader";
import { motion } from "framer-motion";
import { useTransactions, queryKeys } from "@/hooks/queries";

const Dashboard = () => {
  const queryClient = useQueryClient();
  const { data: transactions = [], isLoading } = useTransactions();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [pdfImportOpen, setPdfImportOpen] = useState(false);
  const [importChooserOpen, setImportChooserOpen] = useState(false);
  const [showMoodTracker, setShowMoodTracker] = useState(false);

  const handleTransactionSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
  }, [queryClient]);

  const handleNoMoodLogged = useCallback(() => {
    setShowMoodTracker(true);
  }, []);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-4 max-w-7xl">
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-64 bg-muted animate-pulse rounded" />
          </div>
          <SkeletonLoader variant="stats" count={4} />
          <SkeletonLoader variant="list" count={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
              <p className="text-sm text-muted-foreground">Overview of your financial activity</p>
            </div>
            <MoodIndicator onNoMoodLogged={handleNoMoodLogged} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <QuickActions 
              onAddTransaction={() => setAddDialogOpen(true)}
              onImport={() => (FEATURE_STATEMENT_PDF ? setImportChooserOpen(true) : setImportDialogOpen(true))}
              onTransfer={() => setTransferDialogOpen(true)}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <DashboardStats />
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-3 items-start">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {showMoodTracker && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.22 }}
              >
                <MoodTracker onMoodLogged={() => setShowMoodTracker(false)} />
              </motion.div>
            )}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <TransactionList
                transactions={transactions}
                onAddTransaction={() => setAddDialogOpen(true)}
              />
            </motion.div>
          </div>

          {/* Insights rail */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.25 }}
            >
              <GoalSpotlight />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <DailyImpulse />
            </motion.div>
          </div>
        </div>

        <TransactionAddDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          onSuccess={handleTransactionSuccess}
          onSwitchToTransfer={() => setTransferDialogOpen(true)}
        />

        <TransferDialog
          open={transferDialogOpen}
          onOpenChange={setTransferDialogOpen}
          onSuccess={handleTransactionSuccess}
          onSwitchToTransaction={() => setAddDialogOpen(true)}
        />

        <BankStatementImport
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          onSuccess={handleTransactionSuccess}
        />

        {FEATURE_STATEMENT_PDF && (
          <>
            <ImportSourceDialog
              open={importChooserOpen}
              onOpenChange={setImportChooserOpen}
              onPick={(source) => {
                setImportChooserOpen(false);
                if (source === "pdf") setPdfImportOpen(true);
                else setImportDialogOpen(true);
              }}
            />
            <Suspense fallback={null}>
              {pdfImportOpen && (
                <PdfStatementImport
                  open={pdfImportOpen}
                  onOpenChange={setPdfImportOpen}
                  onSuccess={handleTransactionSuccess}
                />
              )}
            </Suspense>
          </>
        )}

        <FloatingChatBot transactions={transactions} />
      </motion.div>
    </div>
  );
};

export default Dashboard;
